import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Architectural Design',
    description: 'Custom architectural solutions that seamlessly integrate with your existing home.'
  },
  {
    title: 'Permit Management',
    description: 'Complete handling of all necessary permits and approvals for your project.'
  },
  {
    title: 'Foundation Work',
    description: 'Expert foundation construction ensuring structural integrity and longevity.'
  },
  {
    title: 'Custom Millwork',
    description: 'Bespoke millwork and trim details matching your home\'s architectural style.'
  },
  {
    title: 'HVAC Integration',
    description: 'Seamless integration of heating, cooling, and ventilation systems.'
  },
  {
    title: 'Interior Finishing',
    description: 'Premium interior finishes matching or exceeding your existing home\'s quality.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Master Suite Addition'
  },
  {
    url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Family Room Extension'
  },
  {
    url: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Kitchen & Living Addition'
  }
];

export function HomeAdditionsPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Custom Home Additions in Westchester County | Golden Touch</title>
        <meta name="description" content="Expert home addition services in Westchester County & Greenwich. Specializing in seamless architectural integration and luxury expansions." />
        <meta name="keywords" content="home additions Westchester, house extension Greenwich, room addition Scarsdale, luxury home expansion Rye" />
        <link rel="canonical" href="https://renovationwestchester.com/services/home-additions-westchester" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Custom Home Additions in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Expand your living space with architecturally stunning additions that seamlessly 
                integrate with your home. Serving distinguished residences throughout Westchester 
                County and Lower Connecticut.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Custom Home Addition in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Premium Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Expand Your Home?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your vision for expanding your living space. Our expert team is ready 
              to create a seamless addition that enhances your home's beauty and functionality.
            </p>
            <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}